import React, { useState, useEffect } from "react";
import { connection } from "../../../../constants/constants";
import {
  Transaction,
  PublicKey,
  SystemProgram,
  Keypair,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
} from "@solana/spl-token";
import IDL from "../../../../constants/token_idl.json";
import { Program, BN, AnchorProvider } from "@coral-xyz/anchor";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Button from "react-bootstrap/Button";
import messages from "../../../../constants/messages";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import axios from "axios";

const getAssociatedTokenAccount = async (mint, owner, payer) => {
  let tokenAccountAddress = await getAssociatedTokenAddress(mint, owner, true);

  const accountInfo = await connection.getAccountInfo(tokenAccountAddress);
  if (accountInfo !== null) {
    return [tokenAccountAddress, null];
  }

  let tokenAccountCreationInstruction = createAssociatedTokenAccountInstruction(
    payer,
    tokenAccountAddress,
    owner,
    mint
  );
  return [tokenAccountAddress, tokenAccountCreationInstruction];
};

export const InitializeToken = ({
  wallet,
  isOpen,
  setIsOpen,
  connected,
  tokenAddress,
  onInitializeSuccess,
  dateArray,
  tokenQuantityArray,
  setMonthlyData,
  months,
  setCurrentStep,
  tokenMintedWallet,
}) => {
  const [program, setProgram] = useState(null);
  const [solReciever, setSolReciever] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const user = useSelector((state) => state.userStore.user);
  const token = user.auth;

  const TOKEN_SALE_PROGRAM_ID = new PublicKey(
    process.env.REACT_APP_TOKEN_SALE_PROGRAM_ID
  );

  const InitialTokenLimit = process.env.REACT_APP_INITIAL_TOKEN_LIMIT;
  const TokenSaleAuthority = process.env.REACT_APP_TOKEN_SALE_AUTHORITY;
  const tokenPrice = process.env.REACT_APP_TOKEN_PRICE;
  const REACT_APP_EXPLORER_BASE_URL = process.env.REACT_APP_EXPLORER_BASE_URL;
  const REACT_APP_TOKEN_ENVIRONMENT = process.env.REACT_APP_TOKEN_ENVIRONMENT;

  const transaction = new Transaction();

  // Initialize Anchor program
  useEffect(() => {
    const initializeProgram = async () => {
      if (wallet) {
        try {
          const provider = new AnchorProvider(connection, wallet, {
            commitment: "confirmed",
          });

          const programInstance = new Program(IDL, provider);
          setProgram(programInstance);
        } catch (error) {
          console.error("Error creating Program: ", error);
        }
      } else {
        return;
      }
    };

    initializeProgram();
  }, [wallet]);

  const handleInitialize = async (e) => {
    e.preventDefault();
    if (solReciever && tokenAddress) {
      const connectedWallet = wallet?.publicKey?.toBase58();
      if (connectedWallet !== tokenMintedWallet) {
        Swal.fire({
          title: "Wallet Mismatch",
          text: "The connected wallet doesn't match the wallet used during token creation. please connect the same wallet to proceed with contract initialization.",
          icon: "warning",
          confirmButtonText: "OK",
        });
        return;
      }
      try {
        if (!program || !wallet) {
          Swal.fire({
            title: messages.titles.error,
            text: messages.errors.errorWalletConnection,
            icon: "error",
            confirmButtonText: messages.buttons.ok,
          });
          return;
        }
        setLoading(true);
        const saleConfig = Keypair.generate();

        // SALE_AUTHORITY use as key to genrate new program id need to change only production
        const [authority, bump] = PublicKey.findProgramAddressSync(
          [Buffer.from(TokenSaleAuthority)],
          TOKEN_SALE_PROGRAM_ID
        );

        const [programTokenAccount, programTokenAccountInstruction] =
          await getAssociatedTokenAccount(
            tokenAddress,
            authority,
            wallet.publicKey
          );

        if (programTokenAccountInstruction) {
          transaction.add(programTokenAccountInstruction);
        }

        const tokenPriceUsd = tokenPrice;
        const mintDecimals = new BN(6);
        const tokenLimit = InitialTokenLimit * 1000000;
        const initialLimit = new BN(tokenLimit);

        const monthlyLimits = tokenQuantityArray.map(
          (value) => new BN(value * 1000000)
        );
        console.log("monthlyLimits", monthlyLimits);

        // const tokenQuantities = Object.assign({}, tokenQuantityArray);
        // console.log("tokenQuantities", tokenQuantities);

        const tokenQuantities = Object.entries(tokenQuantityArray).reduce(
          (acc, [key, value], index) => {
            const ordinalKeys = [
              "1st",
              "2nd",
              "3rd",
              "4th",
              "5th",
              "6th",
              "7th",
              "8th",
              "9th",
              "10th",
              "11th",
              "12th",
              "13th",
              "14th",
            ];
            if (index < ordinalKeys.length) {
              acc[ordinalKeys[index]] = value;
            }
            return acc;
          },
          {}
        );

        console.log("tokenQuantities", tokenQuantities);

        const totalLockedValuesForFirstYear = tokenQuantityArray
          .slice(0, 12)
          .reduce((acc, curr) => acc + curr, 0);

        const totalLockedTokensForFirstYear = new BN(
          totalLockedValuesForFirstYear * 1000000
        );

        const totalLockedValuesForSecondYear = tokenQuantityArray
          .slice(-2)
          .reduce((acc, curr) => acc + curr, 0);

        const totalLockedTokensForSecondYear = new BN(
          totalLockedValuesForSecondYear * 1000000
        );

        const timestampArray = dateArray.map((date) => {
          return Math.floor(new Date(date).getTime() / 1000);
        });

        console.log("timestampArray", timestampArray);

        // const slotReleaseTimes = Object.assign({}, dateArray);

        const slotReleaseTimes = Object.entries(timestampArray).reduce(
          (acc, [key, value], index) => {
            const ordinalKeys = [
              "1st",
              "2nd",
              "3rd",
              "4th",
              "5th",
              "6th",
              "7th",
              "8th",
              "9th",
              "10th",
              "11th",
              "12th",
              "13th",
              "14th",
            ];
            if (index < ordinalKeys.length) {
              acc[ordinalKeys[index]] = value;
            }
            return acc;
          },
          {}
        );
        console.log("slotReleaseTimes", slotReleaseTimes);

        const bnTimestamps = timestampArray.map((value) => new BN(value));

        console.log("bnTimestamps", bnTimestamps);

        const [monthlyLimitsAccount, monthlyLimitsBump] =
          PublicKey.findProgramAddressSync(
            [Buffer.from("monthly_limits_a")],
            program.programId
          );

        const ix = await program.methods
          .initializeSale(tokenPriceUsd, mintDecimals, initialLimit)
          .accounts({
            authority: wallet.publicKey,
            saleConfig: saleConfig.publicKey,
            systemProgram: SystemProgram.programId,
            recipient: solReciever,
            tokenMint: tokenAddress,
          })
          .instruction();
        transaction.add(ix);

        // This instruction is to set the monthly token buying limits in the contract

        const ix2 = await program.methods
          .setMonthlyLimits(monthlyLimits, bnTimestamps)
          .accounts({
            authority: wallet.publicKey,
            saleConfig: saleConfig.publicKey,
            monthlyLimits: monthlyLimitsAccount,
            systemProgram: SystemProgram.programId,
          })
          .instruction();
        transaction.add(ix2);

        let recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
        transaction.recentBlockhash = recentBlockhash;

        transaction.feePayer = wallet.publicKey;
        transaction.partialSign(saleConfig);

        const signature = await window.solana.signAndSendTransaction(
          transaction
        );

        await connection.confirmTransaction(signature.signature, "confirmed");

        const _sign = signature?.signature;

        const details = await getTransactionDetails(_sign);
        details.tokenSaleConfigAddress = saleConfig.publicKey?.toBase58();
        details.tokenAuthority = wallet.publicKey?.toBase58();
        details.solanaReceiverAddress = solReciever;
        details.saleAuthority = authority?.toBase58();
        details.tokenSaleProgramId = TOKEN_SALE_PROGRAM_ID;
        details.tokenAddress = tokenAddress;
        details.tokenQuantities = tokenQuantities;
        details.slotReleaseTimes = slotReleaseTimes;
        await sendTransactionDetails(details);

        if (details?.saleAuthority) {
          onInitializeSuccess();
        }

        Swal.fire({
          title: messages.titles.success,
          text: messages.success.tokenInitializeSuccess,
          icon: "success",
          showCancelButton: true,
          cancelButtonText: messages.buttons.close,
          confirmButtonText: "Check on Explorer",
        }).then((result) => {
          if (result.isConfirmed) {
            if (!_sign) {
              console.error("Signature not available");
              return;
            }
            const url = `${REACT_APP_EXPLORER_BASE_URL}tx/${_sign}?cluster=${REACT_APP_TOKEN_ENVIRONMENT}`;

            window.open(url, "_blank", "noopener,noreferrer");
          }
        });
        setLoading(false);
        setSolReciever("");
        setMonthlyData(months.map(() => ({ date: "", tokenQuantity: "" })));
        setCurrentStep(0);
      } catch (error) {
        Swal.fire({
          title: messages.titles.error,
          text: messages.errors.tokenInitializeFailed,
          icon: "error",
          confirmButtonText: messages.buttons.ok,
        });
        setLoading(false);
      }
    } else {
      setError("Please enter all the required information to proceed");
    }
  };

  const sendTransactionDetails = async (details) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_URL}/deployment/storeTokenBuyContractInitializer`,
        details,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const signature = response.data.TokenTrxSignature;
      Swal.fire({
        title: `${response.data.message}`,
        icon: response.data.status === 200 ? "success" : "error",
        showCancelButton: true,
        cancelButtonText: "Close",
        confirmButtonText: "Check on Explorer",
      }).then((result) => {
        if (result.isConfirmed) {
          if (!signature) {
            console.error("Signature not available");
            return;
          }
          const url = `${REACT_APP_EXPLORER_BASE_URL}tx/${signature}?cluster=${REACT_APP_TOKEN_ENVIRONMENT}`;

          window.open(url, "_blank", "noopener,noreferrer");
        }
      });
    } catch (error) {
      console.error("Error sending transaction details:", error);
      Swal.fire({
        title: "Error!",
        text: `Error: ${error.message}`,
        icon: "error",
        confirmButtonText: "OK",
      });
      setLoading(false);
    }
  };

  const getTransactionDetails = async (signature) => {
    try {
      const transaction = await connection.getTransaction(signature, {
        maxSupportedTransactionVersion: 0,
      });

      const status = await getTransactionStatus(signature);

      if (transaction) {
        const deploymentwalletAddress = wallet.publicKey.toBase58();

        return {
          signature,
          deploymentwalletAddress,
          status,
        };
      }
    } catch (error) {
      console.error("Failed to get Tx details: ", error);
    }
  };

  const getTransactionStatus = async (signature) => {
    try {
      const status = await connection.getSignatureStatus(signature);

      if (status?.value?.err === null) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.error("Transaction failed");
    }
  };

  const handleConnectWallet = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div>
        <h3>Initialize Contract</h3>
        <div className="user-dash-tab-form">
          <Form.Label>
            SOL Reciever Account<span>*</span>
          </Form.Label>
          <InputGroup className="">
            <Form.Control
              type="string"
              name="solReciever"
              aria-label="Number input with dropdown button"
              value={solReciever}
              onChange={(e) => {
                setSolReciever(e.target.value);
                setError("");
              }}
              placeholder="SOL Receiver Account"
              required
            />
          </InputGroup>
        </div>
        <div className="user-dash-tab-form">
          <Form.Label>
            Token Mint Address<span>*</span>
          </Form.Label>
          <InputGroup className="">
            <Form.Control
              type="string"
              name="tokenAddress"
              aria-label="Number input with dropdown button"
              value={tokenAddress}
              placeholder="Token Mint Address"
              required
              disabled
            />
          </InputGroup>
        </div>
        <div className="user-dash-tab-form">
          <Form.Label>
            Initial Daily Token Limit Per User<span>*</span>
          </Form.Label>
          <InputGroup className="">
            <Form.Control
              type="number"
              name="InitialTokenLimit"
              aria-label="Number input with dropdown button"
              value={InitialTokenLimit}
              placeholder="Daily Token Limit"
              required
              disabled
            />
          </InputGroup>
        </div>
        {connected ? (
          <div className="connect-wallet-one">
            <Button onClick={handleInitialize} disabled={loading}>
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "Initialize Contract"
              )}
            </Button>
          </div>
        ) : (
          <div className="connect-wallet-one">
            <button onClick={handleConnectWallet}>Connect Wallet</button>
          </div>
        )}
        <p className="error-message">{error}</p>
      </div>
    </>
  );
};
